/**
 * DRR-213: Assessment Evidence Pipeline — MnCHOICES Drift Detection
 *
 * Hooks for accessing drift detection data from the backend assessment evidence pipeline.
 * Backend continuously accumulates task completion patterns and compares them against
 * the assessed MnCHOICES values to detect when care needs have changed.
 *
 * Key concepts:
 * - Accumulator: Per-resident state tracking confidence scores across 11 MnCHOICES fields
 * - Drift: Gap between assessed value (MnCHOICES) and observed value (task completion patterns)
 * - Confidence: Increases when patterns are consistent, decreases when they change
 */

import {
  useGetAssessmentAccumulatorSummaryQuery,
  useGetAssessmentDriftResultsQuery,
  useGetAssessmentAlertsQuery,
  useGetAssessmentFieldMappingsQuery,
  useGenerateEvidenceReportMutation,
  usePauseAccumulatorMutation,
  useResumeAccumulatorMutation,
  useDismissDriftAlertMutation,
} from '@/generated/graphql';

/**
 * Get accumulator summary for a single resident.
 * Shows overall drift status, field counts, and average confidence.
 */
export function useAccumulatorSummary(tenantId: string, residentId: string) {
  const { data, loading, error, refetch } = useGetAssessmentAccumulatorSummaryQuery({
    variables: { tenantId, residentId },
    skip: !tenantId || !residentId,
  });

  return {
    summary: data?.assessmentAccumulatorSummary ?? null,
    loading,
    error,
    refetch,
  };
}

/**
 * Get per-field drift results for a resident.
 * Returns detailed comparison between assessed and observed values for each MnCHOICES field.
 */
export function useDriftResults(tenantId: string, residentId: string) {
  const { data, loading, error, refetch } = useGetAssessmentDriftResultsQuery({
    variables: { tenantId, residentId },
    skip: !tenantId || !residentId,
  });

  return {
    results: data?.assessmentDriftResults ?? [],
    loading,
    error,
    refetch,
  };
}

/**
 * Get active drift alerts for a facility (aggregated across all residents).
 * Used for the QAPI dashboard view to show fields with high-confidence drift.
 */
export function useDriftAlerts(tenantId: string) {
  const { data, loading, error, refetch } = useGetAssessmentAlertsQuery({
    variables: { tenantId },
    skip: !tenantId,
  });

  return {
    alerts: data?.assessmentAlerts ?? [],
    loading,
    error,
    refetch,
  };
}

/**
 * Get MnCHOICES field mapping definitions.
 * These define how each MnCHOICES field maps to FERP data sources and what thresholds trigger drift.
 */
export function useFieldMappings() {
  const { data, loading, error } = useGetAssessmentFieldMappingsQuery();

  return {
    mappings: data?.assessmentFieldMappings ?? [],
    loading,
    error,
  };
}

/**
 * Combined hook for resident drift detail view.
 * Fetches both summary and per-field drift results.
 */
export function useResidentDrift(tenantId: string, residentId: string) {
  const { summary, loading: summaryLoading, error: summaryError, refetch: refetchSummary } =
    useAccumulatorSummary(tenantId, residentId);

  const { results, loading: resultsLoading, error: resultsError, refetch: refetchResults } =
    useDriftResults(tenantId, residentId);

  return {
    summary,
    results,
    loading: summaryLoading || resultsLoading,
    error: summaryError || resultsError,
    refetch: () => {
      refetchSummary();
      refetchResults();
    },
  };
}

/**
 * Mutations for managing accumulators and generating reports.
 */
export function useAssessmentActions() {
  const [generateReport, { loading: generating }] = useGenerateEvidenceReportMutation();
  const [pauseAccumulator, { loading: pausing }] = usePauseAccumulatorMutation();
  const [resumeAccumulator, { loading: resuming }] = useResumeAccumulatorMutation();
  const [dismissAlert, { loading: dismissing }] = useDismissDriftAlertMutation();

  return {
    generateReport: async (tenantId: string, residentId: string) => {
      const result = await generateReport({
        variables: { input: { tenantId, residentId } },
      });
      return result.data?.generateEvidenceReport ?? null;
    },
    pauseAccumulator: async (tenantId: string, residentId: string, reason: string) => {
      const result = await pauseAccumulator({
        variables: { input: { tenantId, residentId, reason } },
      });
      return result.data?.pauseAccumulator ?? null;
    },
    resumeAccumulator: async (tenantId: string, residentId: string, resetConfidence?: boolean) => {
      const result = await resumeAccumulator({
        variables: { input: { tenantId, residentId, resetConfidence } },
      });
      return result.data?.resumeAccumulator ?? null;
    },
    /** Dismiss a drift alert for a specific field (DRR-232 Track E) */
    dismissDriftAlert: async (
      tenantId: string,
      residentId: string,
      fieldId: string,
      reason?: string,
    ) => {
      const result = await dismissAlert({
        variables: { input: { tenantId, residentId, fieldId, reason } },
      });
      return result.data?.dismissDriftAlert ?? false;
    },
    loading: generating || pausing || resuming || dismissing,
  };
}

/**
 * Severity color mapping for UI display.
 */
export const DRIFT_SEVERITY_COLORS = {
  NONE: 'green',
  MINOR: 'yellow',
  MAJOR: 'red',
} as const;

/**
 * Direction labels for display.
 */
export const DRIFT_DIRECTION_LABELS = {
  INCREASED: 'Needs More Care',
  DECREASED: 'Needs Less Care',
  STABLE: 'Stable',
} as const;
